import { debounce, Selector } from './helpers';
import { KeyboardNavigable, NonRollingControlMode, KeyboardNavigableEventTypes } from './keyboard-navigation';

/**
 * ExtraInfoDropdown element logic is handled here.
 *
 * Component hides a just a native select element under it. All changes that user makes are reflected
 * back to the native select element. This way element can be used as part as a normal form component.
 *
 * HTML is rendered on server side here:
 * {@link https://veho.stage.crasman.cloud/admin/manage/scriptlets/scriptleteditor/html/code/show/aMDLCrnaTjuGT64Ld9fXxw | Mustache template}
 *
 * Demo can be found from here:
 * {@link https://veho-admin.stage.crasman.cloud/fi/system/components/?extrainfodropdown | Demo}
 */
export default class ExtraInfoDropdown {

  /**
   * @property all logic is bind to this element
   */
  private element:HTMLElement;

  /**
   * @property HTMLElement references are stored here for a later use
   */
  private dom:ExtraInfoDropdownDomElements = {};

  /**
   * @property Implements keyboard navigation
   */
  private keyboardNavigable:KeyboardNavigable;

  /**
   * @property Debounced version open component function, fires only once inside given period of time.
   */
  private debouncedOpenComponent:Function;

  /**
   * @property options
   */
  private options:ExtraInfoDropdownOptions;

  /**
   * @constructor
   * @param {HTMLElement} element logic is bind to this element, classes logical scope so to speak :3
   * @param {object} options:ExtraInfoDropdownOptions
   */
  constructor( element:HTMLElement, options:ExtraInfoDropdownOptions ) {

    this.element = element;

    this.options = options;

    this.findElements();
    this.attacEventListeners();
    this.initKeyboarNavigable();
    this.updateSelectedValueText();

    this.debouncedOpenComponent = debounce( this.openComponent.bind(this), 200 );
  }

  /**
   * Initialize keyboard navigation
   */
  private initKeyboarNavigable() {
    this.keyboardNavigable = new KeyboardNavigable(
      new NonRollingControlMode( this.getFocusableElements() ),
      null
    );

    this.keyboardNavigable.bindKeyboardNavitable(this.element);
    this.keyboardNavigable.subscribe( KeyboardNavigableEventTypes.ON_ENTER_PRESSED, this.onEnterPressed.bind(this) );
    this.keyboardNavigable.subscribe( KeyboardNavigableEventTypes.ON_SPACE_PRESSED, this.onEnterPressed.bind(this) );
  }

  /**
   * Return keyboard focusable elements for use in keyboard navigation
   * @returns {HTMLElement[]} focusable elements
   */
  private getFocusableElements():HTMLElement[] {
    return Array.from( this.element.querySelectorAll(this.options.selectors.focusableElement.selector) );
  }

  /**
   * Populate `dom` property with HTMLElements for a later use. This way they are only fetched once during document livecycle.
   */
  private findElements() {
    this.dom.select = this.element.querySelector( this.options.selectors.select.selector );
    this.dom.label = this.element.querySelector( this.options.selectors.label.selector );
    this.dom.value = this.element.querySelector( this.options.selectors.value.selector );
    this.dom.selectedValueText = this.element.querySelector( this.options.selectors.selectedValueText.selector );
    this.dom.optionElementContainer = this.element.querySelector( this.options.selectors.optionElementContainer.selector );
    this.dom.optionElements = this.element.querySelectorAll( this.options.selectors.optionElement.selector );
  }

  /**
   * Bind event listener function to elements
   */
  private attacEventListeners() {
    this.element.addEventListener('click', this.onElementClicked.bind(this));
    window.addEventListener('click', this.onWindowClicked.bind(this));
    this.dom.value.addEventListener('focus', this.onFocused.bind(this));
  }

  /**
   * Method is called when the ExtraInfoDropdown element is focused
   */
  private onFocused() {
    this.debouncedOpenComponent(true);
    this.keyboardNavigable.setFocusedElement( this.getFocusableElements()[0] );
  }

  /**
   * Called when an `div.va-extrainfo-dropdown` element is clicked.
   * @param {PointerEvent} event event object
   */
  private onElementClicked(event:PointerEvent) {
    const target:HTMLElement = event.target as HTMLElement;

    event.stopPropagation();

    if( target.className?.includes( this.options.selectors.value.className ) ) {//Case when a dropdown value is clicked
      this.debouncedOpenComponent( !this.element.classList.contains(this.options.selectors.dropdownOpen.className ) )
    } if( target.className?.includes( this.options.selectors.optionElement.className ) ) {
      this.onOptionClicked( target );
      this.updateSelectedValueText();
    } else if( document.activeElement !== this.dom.value ) {
      this.debouncedOpenComponent( !this.element.classList.contains(this.options.selectors.dropdownOpen.className ) );
    }
  }

  /**
   * Just closes dropdown if document is clicked outside of dropdown
   * @param {PointerEvent} event event object
   */
  private onWindowClicked(event:PointerEvent) {
    const target = event.target as HTMLElement;

    if( !target.closest(this.options.selectors.containerElement.selector) ) {
      this.debouncedOpenComponent(false);
    }
  }

  /**
   * Called when an option is clicked
   * @param {HTMLElement} option option element
   */
  private onOptionClicked( option:HTMLElement ) {
    this.dom.optionElements.forEach( (optionElement:HTMLElement) => {
      const selected = optionElement.dataset.value === option.dataset.value && !optionElement.classList.contains(this.options.selectors.optionElementSelected.className)
      this.selectOption( optionElement, selected );
    });

    this.updateSelectedOption();
    this.updateSelectedValueText();
  }

  /**
   * Update selected option
   */
  private updateSelectedOption() {
    //Reflect changes to underlaying select element
    const optionsArray = Array.from(this.dom.optionElements) as HTMLElement[]

    const chosenOne:HTMLElement = optionsArray.find( (option:HTMLElement):boolean => {
      return option.classList.contains(this.options.selectors.optionElementSelected.className);
    });

    this.dom.select.value = chosenOne?.dataset?.value;
  }

  /**
   * Open or close component
   * @param {boolean} show true to open component false to close it
   */
  private openComponent( show:boolean ) {

    this.element.classList.toggle(this.options.selectors.dropdownOpen.className, show);

    if( show ) {
      this.element.setAttribute('aria-expanded', 'true');
    } else {
      this.element.removeAttribute('aria-expanded');
    }

    //Close other instances
    ExtraInfoDropdown.instances.forEach( instance => {
      if( instance !== this ) {
        instance.element.classList.toggle(this.options.selectors.dropdownOpen.className, false);
        instance.element.removeAttribute('aria-expanded');
      }
    })
  }

  /**
   * Updated selected text to the label and dispatch change event from select element.
   */
  private updateSelectedValueText() {
    const text:string = this.dom.select[ this.dom.select.selectedIndex ]?.innerText ?? this.element.dataset.emptytext;
    this.dom.selectedValueText.innerText = text;
    this.dom.select.dispatchEvent( new Event('change') )
  }

  /**
   * Select or unselect option element
   * @param {HTMLElement} optionElement option element
   * @param {boolean} isSelected was it selected
   */
  private selectOption( optionElement:HTMLElement, isSelected:boolean ) {
    optionElement.classList.toggle(this.options.selectors.optionElementSelected.className,isSelected);

    if( isSelected ) {
      optionElement.setAttribute('aria-selected', 'true');
    } else {
      optionElement.removeAttribute('aria-selected');
    }
  }

  /**
   * Called on key 'Enter' pressed by keyboard navigation.
   * @param {any} payload currently focused element
   */
  private onEnterPressed( payload:any ) {
    const focusedElement = payload.focusedElement as HTMLElement;

    if( focusedElement?.tagName === 'DIV' && focusedElement.classList.contains( this.options.selectors.value.className ) ) {
      this.openComponent( !this.element.classList.contains( this.options.selectors.dropdownOpen.className ) );
    } else if( focusedElement?.tagName === 'DIV' && focusedElement.classList.contains( this.options.selectors.optionElement.className ) ) {

      this.dom.optionElements.forEach( (optionElement:HTMLElement) => {
        const selected = optionElement.dataset.value === focusedElement.dataset.value &&
                        !optionElement.classList.contains(this.options.selectors.optionElementSelected.className)
        this.selectOption( optionElement, selected );
      });

      this.updateSelectedOption();
    }

  }

  static instances:ExtraInfoDropdown[] = [];

  /**
   * Initialize ExtraInfoDropdown
   */
  static init() {
    document.querySelectorAll('.va-extrainfo-dropdown').forEach( (element:HTMLElement ) => {
      ExtraInfoDropdown.instances.push( new ExtraInfoDropdown( element, {
        selectors: {
          containerElement: new Selector( '.va-extrainfo-dropdown' ),
          select: new Selector( 'select' ),
          label: new Selector( '.va-extrainfo-dropdown__label' ),
          value: new Selector( '.va-extrainfo-dropdown__value' ),
          optionElementContainer: new Selector( '.va-extrainfo-dropdown__options' ),
          optionElement: new Selector( '.va-extrainfo-dropdown__option' ),
          optionElementSelected: new Selector( '.va-extrainfo-dropdown__option--selected' ),
          focusableElement: new Selector( '.va-extrainfo-dropdown__value, .va-extrainfo-dropdown__option') ,
          selectedValueText: new Selector( '.va-extrainfo-dropdown__selected-value' ),
          dropdownOpen: new Selector( '.va-extrainfo-dropdown--open' )
        }
      }));
    });
  }
}



/**
 * ExtraInfoDropdown element's internal html elements are defined here.
 */
interface ExtraInfoDropdownDomElements {
  select?: HTMLSelectElement,
  label?: HTMLDivElement,
  value?: HTMLDivElement,
  selectedValueText?: HTMLDivElement,
  optionElementContainer?: HTMLDivElement,
  optionElements?: NodeListOf<Element>
}

export interface ExtraInfoDropdownOptions {
  selectors: {
    containerElement: Selector,
    select: Selector,
    label: Selector,
    value: Selector,
    optionElementContainer: Selector
    optionElement: Selector,
    optionElementSelected: Selector,
    focusableElement: Selector,
    selectedValueText: Selector,
    dropdownOpen: Selector
  }
}
